import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AxsIconComponent } from '@vanguard/axs-angular';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';

import { AdobeLaunchService } from '../../adobe-launch/adobe-launch.service';
import { SharedInfoService } from '../../shared/services/shared-info/shared-info.service';

export type ThesisType = 'profitable' | 'loss' | 'no-difference' | 'error';

@Component({
  selector: 'thesis',
  standalone: true,
  imports: [CommonModule, AxsIconComponent],
  templateUrl: './thesis.component.html',
  styleUrl: './thesis.component.scss',
})
export class ThesisComponent implements OnChanges {
  @Input() type: ThesisType = 'profitable';
  // False while Clear all fields has left inputs blank - shares the error state, but nothing was entered wrong
  @Input() inputsComplete: boolean = true;

  // Dynamic input values
  @Input() rothConversionTaxAmount: string = '$15,000'; // e.g., "$15,000"
  @Input() yearsUntilDist: number = 20; // e.g., 20
  @Input() assetDifference: string = '$25,092'; // e.g., "$25,092" or "$3,207"
  @Input() betrValue: string = '12.35%'; // e.g., "12.35%"
  @Input() paymentMethod: string = 'cash'; // 'cash' | 'ira' | 'tax_efficient' | 'tax_inefficient'

  @Output() openTerminology = new EventEmitter<void>();
  @Output() openBetr = new EventEmitter<void>();
  @Output() openTaxAmount = new EventEmitter<void>();

  content = content.Data.v4_rcc_root.content.thesis_states;
  commonContent = content.Data.v4_rcc_root.content.common;

  constructor(
    private readonly contentService: ContentService,
    clientInfo: SharedInfoService,
    private cdr: ChangeDetectorRef,
    private readonly adobeLaunchService: AdobeLaunchService,
  ) {
    this.contentService.getContent().subscribe((data) => {
      this.content = data.Data.v4_rcc_root.content.thesis_states;
      this.commonContent = data.Data.v4_rcc_root.content.common;
    });

    clientInfo.getGraphData$().subscribe((graphData) => {
      console.log(
        'Using Input details:',
        clientInfo.inputDetails,
        'Received graph data in ThesisComponent:',
        graphData,
      );
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) {
      // Force immediate change detection for faster image switching
      this.cdr.detectChanges();
    }
  }

  get showHeader(): boolean {
    return this.type === 'error';
  }

  get errorDescription(): string {
    return this.inputsComplete
      ? this.content?.error_description
      : this.content?.incomplete_description;
  }

  get yearsText(): string {
    return this.yearsUntilDist === 1
      ? this.content?.year_singular || 'year'
      : this.content?.years_plural || 'years';
  }

  get desirabilityText(): string {
    if (this.type === 'profitable') {
      return this.content?.desirability_positive || 'desirable today';
    } else if (this.type === 'loss' || this.type === 'no-difference') {
      return this.content?.desirability_negative || 'undesirable today';
    }
    return '';
  }

  get paymentMethodText(): string {
    const methodMap: { [key: string]: string } = {
      cash: this.content?.payment_cash || 'cash.',
      ira: this.content?.payment_ira || 'IRA (assuming no penalties).',
      tax_efficient: this.content?.payment_tax_efficient || 'a tax-efficient account.',
      tax_inefficient: this.content?.payment_tax_inefficient || 'a tax-inefficient account.',
    };
    return methodMap[this.paymentMethod] || methodMap['cash'];
  }

  get paymentArticle(): string {
    if (this.paymentMethod === 'ira') {
      return this.content?.an || 'an';
    }
    if (this.paymentMethod === 'tax_efficient' || this.paymentMethod === 'tax_inefficient') {
      return this.content?.a;
    }

    return '';
  }

  get paymentPreposition(): string {
    // Cash uses "with", others use "from"
    if (this.paymentMethod === 'cash') {
      return this.content?.paying_with || 'if paying conversion taxes with';
    }
    return this.content?.paying_from || 'if paying conversion taxes from';
  }

  onTerminologyClick(): void {
    this.openTerminology.emit();
  }

  onBetrClick(): void {
    this.openBetr.emit();
    this.adobeLaunchService.captureAction('chart:terms-and-definitions', 'link', 'betr calculator');
  }

  onTaxAmountClick(): void {
    this.openTaxAmount.emit();
    this.adobeLaunchService.captureAction('chart:terms-and-definitions', 'link', 'betr calculator');
  }
}
